import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Urls } from 'data';
import { Storage, validateEmail, formatDate } from 'helpers';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import LegalNotice from './components/LegalNotice';

const CONST_USER = 'user';
const CONST_USER_TOKEN = 'user_token';
const CONST_LEGAL_ACCEPTED = 'legal_accepted';
const CONST_LEGAL_DOWNLOADED = 'legal_downloaded';
const CONST_LEGAL_ACCEPTANCE_DATE = 'legal_acceptance_date';

const Login = () => {
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');
  const [displayPwd, setDisplayPwd] = useState(false);
  const storage = new Storage(localStorage, 'eHadj-');
  const [LegalNoticeOpen, setLegalNoticeOpen] = useState(true);
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;
  const isEmptyField = email.trim() === '' || pwd.trim() === '';
  const isDisabled = isEmptyField || isMutating;
  const isLegalAccepted = storage.getData(CONST_LEGAL_ACCEPTED) === true;
  const isLegalDownloaded = storage.getData(CONST_LEGAL_DOWNLOADED) === true;

  const downloadConsentReceipt = () => {
    if (!isLegalDownloaded) {
      const pdfUrl = `${process.env.PUBLIC_URL}pdfs/legal-consent.pdf`;
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = Locales.base.legals.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      storage.set(CONST_LEGAL_DOWNLOADED, true);
    }
  };

  const { mutate: logUser } = useMutation({
    mutationFn: () => {
      const headers = {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Headers': 'Content-Type, Authorization',
      };

      return api.post(
        'auth/user_token/',
        {
          email,
          password: pwd,
        },
        { headers }
      );
    },
    onSuccess: ({ data }) => {
      setError('');
      storage.set(CONST_USER_TOKEN, data.auth_key);
      storage.set(CONST_USER, JSON.stringify(data.user));
      downloadConsentReceipt();
      window.location = Urls.base;
    },
    onError: () => {
      setError(Locales.auth.errors.invalid_identifiers);
      setPwd('');
      setEmail('');
    },
  });

  const handleLogin = (event) => {
    event.preventDefault();
    const validEmail = validateEmail(email);

    if (!validEmail) {
      setError(Locales.auth.errors.invalid_email);
      setPwd('');
      setEmail('');
      return;
    }

    setError('');
    logUser();
  };

  const toggleDisplayPassword = () => setDisplayPwd((val) => !val);

  const toggleLegalNoticeOpen = () => setLegalNoticeOpen((val) => !val);

  const acceptCgu = () => {
    storage.set(CONST_LEGAL_ACCEPTED, true);
    const acceptanceDateTime = formatDate(new Date(), 'dddd DD MMMM YYYY [à] HH:mm:ss');
    storage.set(CONST_LEGAL_ACCEPTANCE_DATE, acceptanceDateTime);
    toggleLegalNoticeOpen();
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-hajj-muted via-white to-green-50 flex items-center justify-center p-4 md:pl-[440px]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-hajj-primary/5 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-green-200/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>

      {isLegalAccepted && (
        <div className="relative w-full max-w-md animate-scale-in">
          <div className="space-y-8">
            <div className="text-center space-y-3">
              <h1 className="text-4xl font-bold text-gray-900 tracking-tight">{Locales.auth.login}</h1>
              <p className="text-base text-gray-600 max-w-sm mx-auto">{Locales.auth.login_description}</p>
            </div>

            {error && (
              <Alert variant="destructive" className="animate-slide-up">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {Locales.base.field.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={Locales.base.field.placeholder.email}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  className="h-14 text-lg bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-hajj-primary focus:ring-4 focus:ring-hajj-primary/10 transition-all duration-200 rounded-xl"
                  autoFocus
                />
              </div>

              <div className="space-y-3">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  {Locales.base.field.password}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={displayPwd ? 'text' : 'password'}
                    placeholder="••••••••••••••••••"
                    value={pwd}
                    onChange={(e) => {
                      setPwd(e.target.value);
                      setError('');
                    }}
                    className="h-14 text-lg bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-hajj-primary focus:ring-4 focus:ring-hajj-primary/10 transition-all duration-200 rounded-xl pr-12"
                  />
                  <button
                    type="button"
                    onClick={toggleDisplayPassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {displayPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="text-left">
                <Link
                  to={Urls.front.password.forgot}
                  className="text-sm text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                >
                  {Locales.auth.forgot_pwd.subtitle}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-medium bg-hajj-primary hover:bg-hajj-secondary text-white rounded-xl shadow-lg shadow-hajj-primary/20 hover:shadow-xl hover:shadow-hajj-primary/30 transition-all duration-200"
                disabled={isDisabled}
              >
                {isMutating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  Locales.auth.connect
                )}
              </Button>
            </form>

            <div className="text-center text-sm text-gray-600">
              <p>
                {Locales.formatString(Locales.auth.legals.auth_disclaimer, {
                  cgu: (
                    <a
                      href={Urls.cgu}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                    >
                      {Locales.auth.legals.cgu}
                    </a>
                  ),
                  mentions: (
                    <a
                      href={Urls.legal_mention}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                    >
                      {Locales.auth.legals.mentions}
                    </a>
                  ),
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {LegalNoticeOpen && !isLegalAccepted && <LegalNotice cta={acceptCgu} />}
    </div>
  );
};

export default Login;
